import { logError } from "@lib/logger";
import { logoutSvg } from "@/assets/images/svgFiles";
import { IEventDataSource } from "@/types/interfaces";
import { BigBashEvent } from "@/types/types";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("events-page")
class EventsPage extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			.container {
				max-width: 1200px;
				min-width: 1000px;
				border: var(--border-default);
				border-radius: var(--border-radius);
				background: white;
				box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
				padding: 16px;
				display: flex;
				flex-direction: column;
				gap: 16px;
				align-items: center;
				justify-content: center;
			}

			.container-middle {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}

			.container-bottom {
				display: flex;
				flex-direction: column;
				gap: 16px;
				background: var(--color-pink);
			}

			.container > * {
				width: 100%;
			}

			.container-top__title-logout {
				display: flex;
				align-items: center;
				justify-content: space-between;
			}

			.logout-btn {
				display: flex;
				align-items: center;
				gap: 6px;
				background: none;
				border: none;
				cursor: pointer;
				font: inherit;
				color: var(--color-text, #333);
			}

			.logout-btn svg {
				width: 20px;
				height: 20px;
			}

			.version-badge {
				position: fixed;
				bottom: 12px;
				left: 12px;
				font-size: 11px;
				font-family: var(--font-text);
				color: white;
				pointer-events: none;
				user-select: none;
			}
		`,
	];

	@state() private events: BigBashEvent[] = [];
	@state() private searchTerm = '';
	@state() private isLoading = true;
	@property({ type: Object }) private eventsDataSource: IEventDataSource;

	constructor(dataSource: IEventDataSource) {
		super();
		this.eventsDataSource = dataSource;
	}

	connectedCallback() {
		super.connectedCallback();
		this._tryToLoadEvents();
	}

	private async _tryToLoadEvents() {
		this.isLoading = true;
		try {
			this.events = await this.eventsDataSource.getAllEvents();
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private _handleFetchError(error: unknown): void {
		logError("Error fetching events:", error);
		alert("Failed to load events. Please try again later.");
	}

	private readonly _handleSearch = (event: Event) => {
		const target = event.target as HTMLInputElement;
		this.searchTerm = target.value.toLowerCase();
	};

	private readonly _handleCreateEvent = () => {
		window.location.href = '/host/create-event';
	};

	// Neue clientseitige Navigation zur Quiz-Startseite
	private readonly _handleNavigateToQuizStart = () => {
		const state = {};
		window.history.pushState(state, '', '/quiz-start');
		window.dispatchEvent(new PopStateEvent('popstate'));
	};

	private readonly _handleLogout = async () => {
		try {
			await fetch("/api/auth/logout/", {
				method: "POST",
				credentials: "include",
			});
		} catch (err) {
			logError("Logout failed:", err);
		} finally {
			// Redirect to login
			window.history.pushState({}, "", "/host/events");
			window.dispatchEvent(new PopStateEvent("popstate"));
		}
	};

	render() {
		if (this.isLoading) {
			return html`
				<div class="container">
					<div style="text-align: center; padding: 60px;">
						Loading events...
					</div>
				</div>
			`;
		}

		const buildTime = import.meta.env.VITE_BUILD_TIME ?? 'dev';

		return html`
			<span class="version-badge">${buildTime}</span>
			<div class="container">
				<div class="container-top">
					<div class="container-top__title-logout">
						<h1 class="text-headline">Event Übersicht</h1>
						<button class="logout-btn" @click=${this._handleLogout}>
							${logoutSvg}
						</button>
					</div>
					<p class="text-body">
						Verwalten Sie Ihre Events - Erstellen, Bearbeiten und Moderieren
						</p>
				</div>

				<div class="container-middle">
					<success-button @click="${this._handleCreateEvent}">
						Neues Event erstellen
					</success-button>
					<!-- Neuer Button: clientseitig zur Quiz-Startseite -->
					<success-button @click="${this._handleNavigateToQuizStart}">
						Zur Quiz-Startseite
					</success-button>
					<!-- <basic-search-input
						placeholder="Events durchsuchen..."
						@search-change="${(e: CustomEvent) =>
						(this.searchTerm = e.detail.value.toLowerCase())}"
					></basic-search-input> -->
				</div>

				<div class="container-bottom">
					${this.events.length > 0
						? html`
								<events-table
									.events=${this.events}
									.eventsDataSource=${this.eventsDataSource}
								></events-table>
							`
						: html`
								<empty-events-table-card
									@create-event=${this._handleCreateEvent}
								></empty-events-table-card>
							`}
				</div>
			</div>
		`;
	}
}

export default EventsPage;
